import React, { useCallback, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { colors } from '@/assets/styles/colors';
import { Text } from '@/components/ui/text/Text';
import type { TableColumn, TableAction } from '@/components/ui/tables/Table';
import type { SimpleFilter } from '@/components/ui/filters/FilterDropdown';
import type { SelectorOption } from '@/components/ui/inputs/selector';
import { employeeContractService, type EmployeeContract } from '@/api/domains/employeeContract';
import type { ContractStatusFilter } from '@/api/domains/employee';
import { useUI } from '@/hooks/ui/useUI';
import { useEmployeeLookupSelector } from '@/hooks/employee/useEmployeeLookupSelector';
import { useTranslation } from '@/i18n';
import { useAppSelector } from '@/hooks';
import { ROUTE_DEFINITIONS } from '@/routes';
import { usePaginatedList } from '@/hooks/ui/usePaginatedList';
import type { PaginatedFetchParams, PaginatedFetchResult } from '@/hooks/ui/usePaginatedList';
import { formatEmployeeIdentity } from '@/utils';


// Filters state
interface EmployeeContractFiltersState {
  contractStatusFilter: ContractStatusFilter;
}

interface UseEmployeeContractListResult {
  rows: EmployeeContract[];
  total: number;
  page: number;
  pageSize: number;
  setPage: (page: number) => void;
  setPageSize: (size: number) => void;
  filters: EmployeeContractFiltersState;
  setFilters: React.Dispatch<React.SetStateAction<EmployeeContractFiltersState>>;
  filterConfig: SimpleFilter[];
  columns: TableColumn<EmployeeContract>[];
  actions: TableAction<EmployeeContract>[];
  onSearch: (term: string) => void;
  onSortChange: (columnKey: string, direction: 'asc' | 'desc') => void;
  loading: boolean;
  selectedEmployeeId: string;
  selectedEmployeeLabel: string;
  handleEmployeeChange: (employeeId: string) => void;
  createContractUrl: string;
  dynamicTitle: string | undefined;
  // Employee lookup selector props (for Selector with pagination)
  employeeSelectorProps: {
    options: SelectorOption[];
    isLoading: boolean;
    hasNextPage: boolean;
    isLoadingMore: boolean;
    onLoadMore: () => Promise<void>;
    onSearchChange: (searchText: string) => void;
  };
}

export function useEmployeeContractList(): UseEmployeeContractListResult {
  const { showNotification, showApiError, openModal, closeModal } = useUI();
  const navigate = useNavigate();
  const { t: translate } = useTranslation();
  const syncAssistance = useAppSelector((state) => state.auth.user?.syncAssistance ?? false);
  const [searchParams] = useSearchParams();

  const initialEmployeeId = useMemo(() => searchParams.get('employeeId'), [searchParams]);

  const {
    options: employeeOptions,
    isLoading: isLoadingEmployees,
    hasNextPage: employeeHasNextPage,
    isLoadingMore: employeeIsLoadingMore,
    onLoadMore: employeeOnLoadMore,
    onSearchChange: employeeOnSearchChange,
    selectedEmployee,
    setSelectedEmployee,
  } = useEmployeeLookupSelector({ initialEmployeeId });

  const selectedEmployeeId = selectedEmployee?.id || '';
  const selectedEmployeeLabel = selectedEmployee?.name || '';

  const fetchFn = useCallback(
    async (
      params: PaginatedFetchParams<EmployeeContractFiltersState>,
      _signal: AbortSignal,
    ): Promise<PaginatedFetchResult<EmployeeContract> | null> => {
      if (!selectedEmployeeId) {
        return { items: [], totalRecords: 0 };
      }
      const response = await employeeContractService.list({
        employeeId: Number(selectedEmployeeId),
        page: params.page,
        pageSize: params.pageSize,
        searchText: params.searchText,
        contractStatusFilter: params.filters.contractStatusFilter,
        sort: params.sort
          ? { key: params.sort.key, direction: params.sort.direction.toLowerCase() as 'asc' | 'desc' }
          : undefined,
      });
      if (!response.success || !response.data) {
        showApiError(response, translate('employee:contracts.messages.loading'));
        return null;
      }
      return { items: response.data.data, totalRecords: response.data.totalRecords };
    },
    [selectedEmployeeId, showApiError, translate],
  );

  const {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    loading,
    onSearch,
    onSortChange,
    refetch,
    filters,
    setFilters,
  } = usePaginatedList<EmployeeContract, EmployeeContractFiltersState>({
    fetchFn,
    initialFilters: { contractStatusFilter: 'ALL' },
    initialSort: { key: 'contractNumberREX', direction: 'ASC' },
    orderByMap: {
      contractNumber: 'contractNumberREX',
      contractName: 'contractNameREX',
      status: 'contractStatus',
      startDate: 'contractStartDate',
      endDate: 'contractEndDate',
      companyName: 'companyName',
      contractType: 'contractType',
    },
    screenCode: ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS.code,
  });

  const handleEmployeeChange = useCallback((employeeId: string) => {
    setSelectedEmployee(employeeId);
    setPage(1);
  }, [setSelectedEmployee, setPage]);

  const handleDelete = useCallback(
    (row: EmployeeContract) => {
      const modalId = `delete-contract-${row.contractId}`;
      const isActive = row.status === 'active';
      
      const confirmDelete = async () => {
        const response = await employeeContractService.delete({
          employeeId: Number(selectedEmployeeId),
          contractId: row.contractId,
          contractNumberREX: row.contractNumber,
        });
        
        if (response.success) {
          showNotification('success', translate('employee:contracts.messages.deleteSuccess'), 5000);
          refetch();
        } else {
          showApiError(response, translate('employee:contracts.messages.deleteFailed'));
        }
      };
      
      openModal(
        'ConfirmModal',
        {
          modalId,
          variant: isActive ? 'error' : 'alert',
          icon: 'alert',
          title: translate('employee:contracts.messages.deleteTitle'),
          ...(isActive && {
            bannerText: translate('employee:contracts.messages.deleteActiveWarning'),
            bannerIcon: 'alert' as const,
          }),
          bodyText: translate('employee:contracts.messages.deleteMessage', { 
            number: row.contractNumber, 
            name: row.contractName,
          }),
          onConfirm: confirmDelete,
          onClose: () => closeModal(modalId),
        },
        true,
      );
    },
    [
      selectedEmployeeId,
      translate,
      refetch,
      openModal,
      closeModal,
      showNotification,
      showApiError,
    ],
  );

  const contractStatusOptions: SimpleFilter['options'] = useMemo(() => ([
    { value: 'ACTIVE', label: translate('employee:filters.contractStatus.options.active') },
    { value: 'INACTIVE', label: translate('employee:filters.contractStatus.options.inactive') },
    { value: 'ALL', label: translate('employee:filters.contractStatus.options.all') },
  ]), [translate]);

  const filterConfig: SimpleFilter[] = useMemo(() => ([
    {
      key: 'contractStatusFilter',
      label: translate('employee:filters.contractStatus.label'),
      multiple: false,
      options: contractStatusOptions,
      defaultValue: 'ALL',
      placeholder: translate('employee:filters.contractStatus.label'),
    },
  ]), [contractStatusOptions, translate]);

  const columns: TableColumn<EmployeeContract>[] = useMemo(
    () => [
      {
        key: 'contractNumber',
        header: translate('employee:contracts.fields.number'),
        sortable: true,
        render: (row: EmployeeContract) => row.contractNumber,
      },
      {
        key: 'contractName',
        header: translate('employee:contracts.fields.name'),
        sortable: true,
        maxWidth: 200,
        render: (row: EmployeeContract) => (
          <Text
            variant="subheader-medium"
            color={colors.important.main}
            as="span"
            onClick={() => {
              if (selectedEmployeeId) {
                const url = ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS_EDITAR.path
                  .replace(':employeeId', selectedEmployeeId)
                  .replace(':contractId', String(row.contractId));
                void navigate(url);
              }
            }}
            className="cursor-pointer transition-colors hover:opacity-80"
          >
            {row.contractName}
          </Text>
        ),
      },
      {
        key: 'status',
        header: translate('employee:contracts.fields.status'),
        render: (row: EmployeeContract) => (
          <Text variant="subheader-regular" as="span">
            {translate(`employee:contracts.status.${row.status}`)}
          </Text>
        ),
      },
      {
        key: 'startDate',
        header: translate('employee:contracts.fields.startDate'),
        sortable: true,
        render: (row: EmployeeContract) => row.startDate,
      },
      {
        key: 'endDate',
        header: translate('employee:contracts.fields.endDate'),
        sortable: true,
        render: (row: EmployeeContract) => row.endDate || '-',
      },
      {
        key: 'companyName',
        header: translate('employee:contracts.fields.company'),
        sortable: true,
        render: (row: EmployeeContract) => row.companyName,
      },
      {
        key: 'contractType',
        header: translate('employee:contracts.fields.type'),
        sortable: true,
        render: (row: EmployeeContract) => translate(`employee:contracts.type.${row.contractType}`),
      },
    ],
    [navigate, selectedEmployeeId, translate],
  );

  const actions: TableAction<EmployeeContract>[] = useMemo(
    () => {
      const baseActions: TableAction<EmployeeContract>[] = [
        {
          id: 'edit',
          label: translate('common:tableActions.edit'),
          icon: 'edit',
          onClick: (row) => {
            if (selectedEmployeeId) {
              const url = ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS_EDITAR.path
                .replace(':employeeId', selectedEmployeeId)
                .replace(':contractId', String(row.contractId));
              void navigate(url);
            }
          },
        },
      ];

      // Only add delete action if syncAssistance is false
      if (!syncAssistance) {
        baseActions.push({
          id: 'delete',
          label: translate('common:tableActions.delete'),
          icon: 'close',
          onClick: (row) => {
            void handleDelete(row);
          },
          variant: 'danger',
        });
      }

      return baseActions;
    },
    [navigate, selectedEmployeeId, translate, handleDelete, syncAssistance],
  );

  // URL para crear contrato
  const createContractUrl = useMemo(() => {
    if (selectedEmployeeId) {
      return ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS_CREAR.path.replace(':employeeId', selectedEmployeeId);
    }
    return ROUTE_DEFINITIONS.EMPLEADO_CONTRATOS_CREAR.path;
  }, [selectedEmployeeId]);

  // Título dinámico para breadcrumb: el nombre ya viene en su orden de
  // presentación desde el mapper del lookup; aquí solo se le adosa el RUT.
  const dynamicTitle = useMemo(() => {
    const identity = formatEmployeeIdentity(selectedEmployeeLabel, selectedEmployee?.rut);
    return identity ? translate('employee:breadcrumb.contracts', { identity }) : undefined;
  }, [selectedEmployeeLabel, selectedEmployee?.rut, translate]);

  // Props for employee Selector with pagination
  const employeeSelectorProps = useMemo(() => ({
    options: employeeOptions,
    isLoading: isLoadingEmployees,
    hasNextPage: employeeHasNextPage,
    isLoadingMore: employeeIsLoadingMore,
    onLoadMore: employeeOnLoadMore,
    onSearchChange: employeeOnSearchChange,
  }), [
    employeeOptions,
    isLoadingEmployees,
    employeeHasNextPage,
    employeeIsLoadingMore,
    employeeOnLoadMore,
    employeeOnSearchChange,
  ]);

  return {
    rows,
    total,
    page,
    pageSize,
    setPage,
    setPageSize,
    filters,
    setFilters,
    filterConfig,
    columns,
    actions,
    onSearch,
    onSortChange,
    loading,
    selectedEmployeeId,
    selectedEmployeeLabel,
    handleEmployeeChange,
    createContractUrl,
    dynamicTitle,
    employeeSelectorProps,
  };
}

export default useEmployeeContractList;
